/**
 * verify-grading — 圖表題批改的「驗算第二關」。
 *
 * 緣由(boss 2026-06-11「兩個根本不一樣」):視覺模型邊看圖邊批改不穩 —
 * 實測會把學生筆跡照抄成正解(永遠判對)、或讀錯數字後自圓其說。但同一份
 * 表格資料一旦轉成結構化 JSON,用文字模型「重算正解再比對」就穩定得多。
 *
 * 作法:對「有圖表 + 讀得到學生作答」的 fill 題,逐題丟
 * 題目 + 圖表 JSON + 學生作答 → textComplete(bridge)→ 拿回獨立算出的
 * 正解與判定,覆蓋 OCR 第一關的結果。併發池 3 路(同 variant-practice 教訓,
 * 整批平行會打爆單機 bridge)。任一題驗算失敗 → 保留第一關結果,不阻斷。
 *
 * 鐵則:純學習側、best-effort、絕不 throw。
 */

import type { ExtractedItem } from './ocr-client';
import { textComplete } from '../text-complete-client';
import { coerceJsonObject } from '../llm-json';

/** 同時驗算幾題(單機 bridge 一發 ~20s,3 路是吞吐與穩定的平衡)。 */
const VERIFY_CONCURRENCY = 3;

/** 這題需要進第二關嗎:fill + 有圖表 + 讀得到學生作答。 */
function needsVerify(it: ExtractedItem): boolean {
  return it.kind === 'fill' && !!it.figure && typeof it.studentAnswer === 'string' && it.studentAnswer.trim() !== '';
}

/** 單題驗算:回 null = 驗不出(保留原判)。 */
async function verifyOne(it: ExtractedItem): Promise<{ correctAnswer: string; isCorrect: boolean; explanation?: string } | null> {
  const system =
    '你是台灣國小三年級的數學/各科批改老師。先自己根據圖表資料把正解算出來,再跟學生作答比對。' +
    '只輸出單一 JSON 物件,不要任何多餘文字或 markdown。';
  const prompt =
    '題目(學生已作答,請你獨立重算後批改):\n' +
    `題目:${it.prompt}\n` +
    `圖表資料(JSON):${JSON.stringify(it.figure)}\n` +
    `學生作答:${it.studentAnswer}\n\n` +
    '步驟:1) 只根據圖表資料與題意,自己算出正解(不要看學生寫什麼)。' +
    '2) 比對學生作答與你的正解(數字相同即算對,容忍「7時2分/7:02」這類寫法差異)。\n' +
    '如果圖表資料不完整、欄列對不上、或不足以確定正解 — 不要硬猜,只回 {"unsure":true}。\n' +
    '用這個 JSON 格式回覆(只回 JSON):\n' +
    '{"correctAnswer":"你算出的正解(簡短)","isCorrect":true或false,"explanation":"一句怎麼算"}';

  const raw = await textComplete({ system, prompt });
  if (!raw) return null;
  const o = coerceJsonObject(raw);
  if (!o) return null;
  if (o.unsure === true) return null; // 圖表資料不夠好 → 保留第一關原判,不硬猜

  const correctAnswer =
    typeof o.correctAnswer === 'string' || typeof o.correctAnswer === 'number' ? String(o.correctAnswer).trim() : '';
  if (!correctAnswer || typeof o.isCorrect !== 'boolean') return null;
  return {
    correctAnswer,
    isCorrect: o.isCorrect,
    explanation: typeof o.explanation === 'string' && o.explanation.trim() !== '' ? o.explanation.trim() : undefined,
  };
}

/**
 * 對整批 OCR 結果跑驗算第二關(就地覆蓋 correctAnswer / isCorrect / explanation)。
 * 回傳被覆蓋(驗算成功)的題數。onProgress 回報 (done, total) — total = 需驗算題數。
 */
export async function verifyGradedItems(
  items: ExtractedItem[],
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  const targets = items.map((it, i) => ({ it, i })).filter(({ it }) => needsVerify(it));
  if (targets.length === 0) return 0;

  let done = 0;
  let verified = 0;
  let cursor = 0;
  async function lane(): Promise<void> {
    while (cursor < targets.length) {
      const { it } = targets[cursor++];
      const v = await verifyOne(it).catch(() => null);
      done++;
      if (v) {
        it.correctAnswer = v.correctAnswer;
        it.isCorrect = v.isCorrect;
        if (v.explanation) it.explanation = v.explanation;
        verified++;
      }
      onProgress?.(done, targets.length);
    }
  }
  await Promise.all(Array.from({ length: Math.min(VERIFY_CONCURRENCY, targets.length) }, () => lane()));
  return verified;
}
